import { UseFormReturn } from 'react-hook-form';
import { IColumn, IRow } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import Column from '../Column/Column';
import './Row.scss';
import { IUniqueKey } from '@molecules/formbuilder/interfaces/IUniqueKey';
import { IDocumentScheme } from '@models/document/IDocumentScheme';
import { useActivated, useFormulaState, useFormulaStateWithStatus } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import { DocumentFormulaTraceParentContext, formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { CSSProperties, useCallback, useContext, useEffect, useState } from 'react';
import { gridGap, gridTrackCount, resolveGridSpans } from '@srm/core/contracts/forms';

export interface IRowProps<TFieldValues extends object = object> extends IUniqueKey {
    row?: IRow;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    docId?: string;
    docScheme?: IDocumentScheme;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

interface IGridColumnProps<TFieldValues extends object = object> extends IRowProps<TFieldValues> {
    column: IColumn;
    index: number;
    span: number | null;
    spanSm: number | null;
    onHidden: (index: number, hidden: boolean) => void;
}

const GridColumn = <TFieldValues extends object = object>({
    column,
    index,
    span,
    spanSm,
    onHidden,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    uniqueKey,
    docScheme,
    ensureExternalDataSourceLoaded,
}: IGridColumnProps<TFieldValues>) => {
    const { activated } = useActivated();
    const { value, settled } = useFormulaStateWithStatus(
        column.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: column.id
                ? {
                      targetId: column.id,
                      targetKind: 'column',
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const hidden = Boolean(column.visibilityRules) && settled && !value;

    useEffect(() => {
        onHidden(index, hidden);
    }, [hidden, index, onHidden]);

    if (hidden || span == null || spanSm == null) return null;

    const style = {
        '--span': span,
        '--span-sm': spanSm,
        ...(column.minWidth ? { '--col-min': column.minWidth } : {}),
        ...(column.maxWidth ? { '--col-max': column.maxWidth } : {}),
    } as CSSProperties;

    return (
        <div className="form-column" style={style} data-span={span}>
            <Column
                column={column}
                fields={fields}
                docId={docId}
                formMethods={formMethods}
                setError={setError}
                isEdit={isEdit}
                isNew={isNew}
                turnOffInvokeChangesOnNew={turnOffInvokeChangesOnNew}
                includedFields={includedFields}
                uniqueKey={uniqueKey}
                docScheme={docScheme}
                ensureExternalDataSourceLoaded={ensureExternalDataSourceLoaded}
            />
        </div>
    );
};

const Row = <TFieldValues extends object = object>({
    row,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    docId,
    uniqueKey,
    docScheme,
    ensureExternalDataSourceLoaded,
}: IRowProps<TFieldValues>) => {
    const { activated } = useActivated();
    const traceParent = useContext(DocumentFormulaTraceParentContext);
    const visibility = useFormulaState(row?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: row?.id
            ? {
                  targetId: row.id,
                  targetKind: 'row',
                  ruleKind: 'visibility',
              }
            : undefined,
    });
    const grid = row?.layout === 'grid';
    const [hiddenFlags, setHiddenFlags] = useState<boolean[]>([]);
    const onHidden = useCallback((index: number, hidden: boolean) => {
        setHiddenFlags((prev) => {
            // нет флага = видима: первый отчёт колонки «не скрыта» ряд не перерисовывает
            if (Boolean(prev[index]) === hidden) return prev;
            const next = prev.slice();
            next[index] = hidden;
            return next;
        });
    }, []);

    useEffect(() => {
        setHiddenFlags((prev) => (prev.length === 0 ? prev : []));
    }, [row?.columns?.length]);

    if (!(row && row.columns && visibility)) return <></>;

    const shared = {
        formMethods,
        setError,
        isEdit,
        isNew,
        turnOffInvokeChangesOnNew,
        fields,
        includedFields,
        docId,
        uniqueKey,
        docScheme,
        ensureExternalDataSourceLoaded,
    };

    if (!grid) {
        return (
            <DocumentFormulaTraceParentContext value={row.id ? { targetId: row.id, targetKind: 'row' } : traceParent}>
                <div
                    className="form-row"
                    data-testid={row.id ? `row-${row.id}` : undefined}
                    {...formulaTraceAttrs(row.id, 'row')}
                >
                    {row.columns.map((item, i) => (
                        <Column column={item} key={uniqueKey + i} {...shared} />
                    ))}
                </div>
            </DocumentFormulaTraceParentContext>
        );
    }

    const hidden = row.columns.map((_, index) => Boolean(hiddenFlags[index]));
    const spans = resolveGridSpans(row.columns, row.gridColumns, hidden, row.collapseHidden === 'grow');
    const spansSm = resolveGridSpans(row.columns, row.gridColumns, hidden, row.collapseHidden === 'grow', true);
    const style = {
        '--form-columns': gridTrackCount(row.gridColumns),
        '--form-gap': gridGap(row.gap),
    } as CSSProperties;

    return (
        <DocumentFormulaTraceParentContext value={row.id ? { targetId: row.id, targetKind: 'row' } : traceParent}>
            <div
                className={`form-row form-row--grid${row.align === 'control' ? ' form-row--align-control' : ''}`}
                style={style}
                data-testid={row.id ? `row-${row.id}` : undefined}
                {...formulaTraceAttrs(row.id, 'row')}
            >
                {row.columns.map((item, i) => (
                    <GridColumn
                        key={uniqueKey + i}
                        column={item}
                        index={i}
                        span={spans[i]}
                        spanSm={spansSm[i]}
                        onHidden={onHidden}
                        {...shared}
                    />
                ))}
            </div>
        </DocumentFormulaTraceParentContext>
    );
};

export default Row;
